/**
 * Deeplink Error Modal Component
 * 
 * Displays an "Oops" modal when a deeplink fails to load,
 * showing the URL and allowing users to copy it.
 */

import React, { useState, useEffect, useContext } from 'react';
import { useActor } from '@xstate/react';
import { GlobalStateContext } from '../state/app/index';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import logger from '../logger';

const DeeplinkErrorModal = () => {
  const globalServices = useContext(GlobalStateContext);
  
  // Guard against undefined context (e.g., during SSR or before provider mounts)
  if (!globalServices || !globalServices.appState || !globalServices.modalState) {
    return null;
  }
  
  const [appState] = useActor(globalServices.appState);
  const [modalState, sendModalState] = useActor(globalServices.modalState);
  const [copied, setCopied] = useState(false);

  const { deeplinkError } = appState.context || {};
  
  // Show modal when deeplink error is set
  useEffect(() => {
    if (deeplinkError && deeplinkError.url && !modalState.matches('active')) {
      const { url, reason, invalidId } = deeplinkError;

      // Determine error message based on reason
      const getErrorMessage = () => {
        switch (reason) {
      case 'note_not_found':
        return `The note "${invalidId}" could not be found. It may have been deleted or you may not have access to it.`;
      case 'notebook_not_found':
        return `The notebook "${invalidId}" could not be found. It may have been deleted or you may not have access to it.`;
      case 'aof_not_found':
        return `The area of focus "${invalidId}" could not be found. It may have been deleted or you may not have access to it.`;
      case 'invalid_url':
        return 'The deeplink URL is invalid or malformed.';
      case 'access_denied':
        return `You don&apos;t have access to "${invalidId}".`;
      default:
        return 'This deeplink could not be loaded. The resource may have been deleted or you may not have access to it.';
        }
      };

      sendModalState({
        type: 'updateModal',
        data: {
          title: 'Oops! Deeplink Not Found',
          content: (
            <div className="deeplink-error-modal">
              <div className="flex items-start mb-4">
                <ErrorOutlineIcon 
                  className="text-orange-500 mr-3 mt-1" 
                  style={{ fontSize: '2rem' }}
                />
                <div className="flex-1">
                  <p className="text-gray-700 mb-3">
                    {getErrorMessage()}
                  </p>
                <p className="text-sm text-gray-600 mb-4">
                  You&apos;ve been taken to the home page. Here&apos;s the deeplink URL you tried to access:
                </p>
                  <div className="bg-gray-100 border border-gray-300 rounded p-3 mb-4">
                    <div className="flex items-center justify-between">
                      <code className="text-sm text-gray-800 break-all flex-1 mr-2">
                        {url}
                      </code>
                      <button
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(url);
                            setCopied(true);
                            logger.info('Deeplink URL copied to clipboard');
                            setTimeout(() => setCopied(false), 2000);
                          } catch (error) {
                            logger.error({ error }, 'Failed to copy deeplink URL');
                          }
                        }}
                        className="flex-shrink-0 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded transition-colors"
                        title="Copy URL"
                        aria-label="Copy deeplink URL"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </button>
                    </div>
                    {copied && (
                      <div className="mt-2 text-sm text-green-600">
                        ✓ Copied to clipboard!
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ),
          showCloseModal: true,
          primaryButton: {
            title: 'Got it',
            show: true,
            callback: () => {
              // Clear the deeplink error from app state
              globalServices.appState.send({
                type: 'clearDeeplinkError',
              });
              // Close the modal
              sendModalState({ type: 'closeModal' });
            },
          },
          secondaryButton: {
            show: false,
          },
        },
      });
    }
  }, [deeplinkError, modalState, globalServices, sendModalState]);

  return null;
};

export default DeeplinkErrorModal;

